import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Mail, Phone } from "lucide-react";

const ECOMMERCE_BROKERS_LOGO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_flip-calc-1/artifacts/gxj7bze1_Ecomm%20brokers%20image.png";
const BONZA_LOGO = "https://customer-assets.emergentagent.com/job_flip-calc-1/artifacts/s4klt4h6_Colour%20Landscape%20Logo.png";

const ValuationDisclaimer = () => {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="valuation-disclaimer-page">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex-shrink-0">
              <img
                src={ECOMMERCE_BROKERS_LOGO}
                alt="Ecommerce Brokers"
                className="h-10 md:h-12 w-auto"
              />
            </Link>
            <Link
              to="/"
              className="text-slate-600 hover:text-blue-600 text-sm font-medium flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
                Valuation Disclaimer
              </h1>
            </div>
            
            <p className="text-slate-500 mb-8">
              Last updated: {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>

            {/* Important Notice Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
              <h2 className="text-lg font-semibold text-amber-800 mb-2">Important Notice</h2>
              <p className="text-amber-700">
                The Ecommerce Brokers online valuation tool provides an <strong>indicative 
                guide only</strong> and should not be considered a formal business valuation, 
                appraisal, or professional advice. The results are estimates based on limited 
                information and general market assumptions.
              </p>
            </div>

            <div className="prose prose-slate max-w-none">
              <h2>1. Nature of the Valuation</h2>
              <p>
                The online valuation tool provided by Ecommerce Brokers is designed to give 
                business owners a general indication of what their ecommerce business might 
                be worth based on the information provided. This is:
              </p>
              <ul>
                <li><strong>Not a formal valuation</strong> – It does not constitute a certified 
                business valuation or appraisal</li>
                <li><strong>Not professional advice</strong> – It should not replace advice from 
                qualified accountants, business valuers, or financial advisors</li>
                <li><strong>An estimate only</strong> – Actual sale prices may vary significantly 
                from the indicated range</li>
                <li><strong>Based on limited information</strong> – A comprehensive valuation 
                requires detailed financial analysis and due diligence</li>
              </ul>

              <h2>2. Factors Not Considered</h2>
              <p>
                The online valuation tool uses simplified assumptions and may not account for:
              </p>
              <ul>
                <li>Detailed financial statement analysis and adjustments</li>
                <li>Quality and accuracy of the information provided</li>
                <li>Current market conditions and buyer demand</li>
                <li>Specific industry or niche factors</li>
                <li>Intellectual property, patents, or proprietary technology value</li>
                <li>Customer contracts, supplier agreements, or partnership arrangements</li>
                <li>Pending legal matters, liabilities, or contingencies</li>
                <li>Quality of inventory, equipment, or other assets</li>
                <li>Key person dependencies and transition requirements</li>
                <li>Local, state, or international market variations</li>
              </ul>

              <h2>3. Information Accuracy</h2>
              <p>
                The valuation result is only as accurate as the information you provide. You 
                are responsible for:
              </p>
              <ul>
                <li>Providing truthful and accurate financial data</li>
                <li>Ensuring figures reflect actual business performance</li>
                <li>Disclosing any unusual circumstances that may affect value</li>
              </ul>
              <p>
                We do not verify the information you submit, and inaccurate inputs will 
                produce inaccurate results.
              </p>

              <h2>4. No Guarantee of Sale Price</h2>
              <p>
                The valuation range provided does not guarantee that:
              </p>
              <ul>
                <li>Your business will sell at the indicated price</li>
                <li>Buyers will agree with the valuation methodology</li>
                <li>Market conditions will support the estimated value</li>
                <li>The business will sell within any particular timeframe</li>
              </ul>
              <p>
                Actual sale prices depend on numerous factors including buyer negotiation, 
                market conditions, due diligence findings, and deal structure.
              </p>

              <h2>5. Valuation Methodology</h2>
              <p>
                Our online tool uses common ecommerce business valuation approaches including:
              </p>
              <ul>
                <li>Earnings multiples based on adjusted profit or owner earnings</li>
                <li>Revenue multiples for high-growth or subscription businesses</li>
                <li>Industry-specific adjustments for business type and model</li>
                <li>Quality adjustments based on growth, margins, and operational factors</li>
              </ul>
              <p>
                Different valuers may use different methodologies, assumptions, and multiples, 
                which may result in materially different valuations.
              </p>

              <h2>6. Professional Valuation Recommended</h2>
              <p>
                Before making any significant decisions about your business, including:
              </p>
              <ul>
                <li>Listing your business for sale</li>
                <li>Setting an asking price</li>
                <li>Negotiating with buyers</li>
                <li>Making financial or tax decisions based on business value</li>
              </ul>
              <p>
                We strongly recommend obtaining a professional valuation from a qualified 
                business valuer, accountant, or business broker who can conduct thorough 
                due diligence and provide advice tailored to your specific circumstances.
              </p>

              <h2>7. Broker Appraisal</h2>
              <p>
                If you choose to engage with a broker through our service, they will 
                typically conduct their own assessment of your business, which may include:
              </p>
              <ul>
                <li>Detailed review of financial statements</li>
                <li>Normalisation of earnings and add-backs</li>
                <li>Market comparison analysis</li>
                <li>Assessment of business risks and opportunities</li>
                <li>Recommendation of an appropriate asking price range</li>
              </ul>
              <p>
                The broker's appraisal may differ from the online valuation result.
              </p>

              <h2>8. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, Ecommerce Brokers and Bonza Business 
                & Franchise Sales:
              </p>
              <ul>
                <li>Accept no responsibility for decisions made based on valuation results</li>
                <li>Make no warranty about the accuracy of the valuation methodology</li>
                <li>Are not liable for any loss or damage arising from use of the tool</li>
                <li>Do not guarantee any particular business outcome</li>
              </ul>

              <h2>9. Use at Your Own Risk</h2>
              <p>
                By using the online valuation tool, you acknowledge and accept that:
              </p>
              <ul>
                <li>The result is an indicative guide only</li>
                <li>You will seek professional advice before making important decisions</li>
                <li>You use the tool and its results at your own risk</li>
                <li>You have read and understood this disclaimer</li>
              </ul>

              <h2>10. Contact Us</h2>
              <p>
                If you have questions about the valuation tool or would like to discuss 
                a professional appraisal, please contact us:
              </p>
              <div className="bg-slate-50 rounded-xl p-6 not-prose">
                <p className="font-semibold text-slate-900 mb-2">Bonza Business & Franchise Sales</p>
                <div className="space-y-2 text-slate-600">
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <a href="mailto:sales@bonzabfs.com.au" className="text-blue-600 hover:underline">
                      sales@bonzabfs.com.au
                    </a>
                  </p>
                  <p className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <a href="tel:1300266922" className="text-blue-600 hover:underline">
                      1300 266 922
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <img src={ECOMMERCE_BROKERS_LOGO} alt="Ecommerce Brokers" className="h-8 brightness-0 invert" />
              <span className="text-sm">Powered by Bonza Business & Franchise Sales</span>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/disclaimer" className="text-white">Disclaimer</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ValuationDisclaimer;
